import React, { useContext } from 'react';
import { toast } from 'react-toastify';
import SimpleBar from 'simplebar-react';
import { ACTIONS } from '../../../../../constants';
import { DownloadReportsModel, EmployeeNodeModel } from '../../../../../models';
import { getEmployeesIds } from '../../../../../utils';
import { getActionService } from '../../../../../utils/getActionService';
import { handleDownloadReport } from '../../../../../utils/handleDownloadReport';
import { AppContext } from '../../../../context';
import { ReportItem } from '../MenuItem';
import { s } from '@fullcalendar/core/internal-common';

interface Props {
    selectedEmployees: EmployeeNodeModel[];
    startDateScheduling: string;
    endDateScheduling: string;

    setIsOpenFilterUnattendanceInterruptionsReportModal: (value: boolean) => void;
    setIsOpenFilterLunchTimeControl: (value: boolean) => void;
    setIsOpenFilterInterruptionsRepotModal: (value: boolean) => void;
    setIsOpenFilterDailyAssistance: (value: boolean) => void;
    setIsOpenFilterWorkingSchedulingsRepotModal: (value: boolean) => void;
}

export const MarkingMenuGroup: React.FC<Props> = ({
    selectedEmployees,
    startDateScheduling,
    endDateScheduling,
    setIsOpenFilterUnattendanceInterruptionsReportModal,
    setIsOpenFilterLunchTimeControl,
    setIsOpenFilterInterruptionsRepotModal,
    setIsOpenFilterDailyAssistance,
    setIsOpenFilterWorkingSchedulingsRepotModal,
}) => {
    const { langResources, downloadReport, userInfo } = useContext(AppContext);

    /**
     * Descarga el reporte de eventos
     */
    const handlerSaveReportAllEvents = () => {       
        handleDownloadReport(
            startDateScheduling,
            endDateScheduling,
            getEmployeesIds(selectedEmployees),
            langResources['reports.message.alert'],
            langResources['events.report.filename'],
            '/jerusalem/api/v2.0.0/reports/all-events',
            downloadReport
        );
    };

    /**
     * Descarga el reporte detallado de programación y eventos
     */
    const handlerSaveReportDetailedProgrammingEventsReport = () => {
        handleDownloadReport(
            startDateScheduling,
            endDateScheduling,
            getEmployeesIds(selectedEmployees),
            langResources['reports.message.alert'],
            'Reporte detallado de programación y eventos',
            '/jerusalem/api/v2.0.0/reports/schedules-and-events',
            downloadReport
        );
    };

    /**
     * Descarga el reporte de tiempo trabajado
     */
    const handlerSaveReportWorkTime = () => {        
        handleDownloadReport(
            startDateScheduling,
            endDateScheduling,
            getEmployeesIds(selectedEmployees),
            langResources['reports.message.alert'],
            langResources['time.worked.report.filename'],
            '/jerusalem/api/v2.0.0/reports/worked-time',
            downloadReport,
            false
        );
    };

    /**
     * Descarga el reporte de llegadas tarde y salidas temprano calculado
     */
    const handlerSaveReportLateArrivalsEarlyDeparturesCalc = () => {
        handleDownloadReport(
            startDateScheduling,
            endDateScheduling,
            getEmployeesIds(selectedEmployees),
            langResources['reports.message.alert'],
            langResources['late.arrivals.earlydepartures.calc.report.filename'],
            '/jerusalem/api/v2.0.0/reports/late-arrivals-early-departures',
            downloadReport,
            false
        );
    };

    /**
     * Descarga el reporte de llegadas tarde y salidas temprano
     */
    const handlerSaveReportLateArrivalsEarlyDepartures = () => {
        handleDownloadReport(
            startDateScheduling,
            endDateScheduling,
            getEmployeesIds(selectedEmployees),
            langResources['reports.message.alert'],
            langResources['late.arrivals.earlydepartures.report.filename'],
            '/jerusalem/api/v2.0.0/reports/late-arrivals-early-departures-group',
            downloadReport
        );
    };

    /**
     * Descarga el reporte de permanencia
     */
    const handlerSaveReportPermanence = () => {
        handleDownloadReport(
            startDateScheduling,
            endDateScheduling,
            getEmployeesIds(selectedEmployees),
            langResources['reports.message.alert'],
            langResources['permanence.report.filename'],
            '/jerusalem/api/v2.0.0/reports/employee-permanence',
            downloadReport,
            false
        );
    };

    /**
     * Descarga el reporte de horas liquidadas por programación
     */
    const handlerSaveReportDettlementScheduling = () => {
        handleDownloadReport(
            startDateScheduling,
            endDateScheduling,
            getEmployeesIds(selectedEmployees),
            langResources['reports.message.alert'],
            'Horas Liquidadas por Programación',
            '/jerusalem/api/v2.0.0/reports/dettlement-of-hours-by-scheduling',
            downloadReport
        );
    };

    /**
     * Descarga el reporte de consolidado de cantidad de personas por turno
     */
    const handlerSaveReportConsolidatedNumberPeoplePerWorkshift = () => {
        handleDownloadReport(
            startDateScheduling,
            endDateScheduling,
            getEmployeesIds(selectedEmployees),
            langResources['reports.message.alert'],
            'Consolidado cantidad de personas por turno',
            '/jerusalem/api/v2.0.0/reports/get-people-by-workshift-id',
            downloadReport
        );
    };

    /**
     * Descarga el reporte de promedio de horas trabajadas
     */
    const handlerSaveReportAverageHoursWork = () => {
        handleDownloadReport(
            startDateScheduling,
            endDateScheduling,
            getEmployeesIds(selectedEmployees),
            langResources['reports.message.alert'],
            langResources['total.hours.worked.filename'],
            '/jerusalem/api/v2.0.0/reports/average-hours-worked',
            downloadReport
        );
    };

    /**
     * Descarga el reporte de horas adicionales e interrupciones
     */
    const handlerSaveReportAdditionalHoursInterruptions = () => {
        handleDownloadReport(
            startDateScheduling,
            endDateScheduling,
            getEmployeesIds(selectedEmployees),
            langResources['reports.message.alert'],
            langResources['additional.hours.interruptions.filename'],
            '/jerusalem/api/v2.0.0/reports/additional-hours-and-interruptions',
            downloadReport
        );
    };

    /**
     * Descarga el reporte de Auditoría de programación de turnos e interrupciones
     */
    const handlerSaveReportWorkshiftAndInterruptionSchedulingAuditReport = () => {
        handleDownloadReport(
            startDateScheduling,
            endDateScheduling,
            getEmployeesIds(selectedEmployees),
            langResources['reports.message.alert'],
            'Reporte de Auditoría de Programación de Turnos e Interrupciones',
            '/jerusalem/api/v2.0.0/reports/audit-of-workshift-and-interruption-scheduling',
            downloadReport
        );
    };

    /**
     * Descarga el reporte de Tiempos de interrupciones
     */
    const handlerSaveReportInterruptionTimes = () => {
        handleDownloadReport(
            startDateScheduling,
            endDateScheduling,
            getEmployeesIds(selectedEmployees),
            langResources['reports.message.alert'],
            langResources['interruptions.times.report.filename'],
            '/jerusalem/api/v2.0.0/reports/time-and-interruption',
            downloadReport
        );
    };

    // Lógica para verificar si se pueden abrir los modales
    const handleReportClick = (modalSetter: Function) => {
        if (
            selectedEmployees.length > 0 &&
            startDateScheduling &&
            endDateScheduling
        ) {
            modalSetter(true);
        } else {
            toast.error(langResources['reports.message.alert'], {
                autoClose: 5000,
                position: toast.POSITION.BOTTOM_RIGHT,
                closeOnClick: true,
                closeButton: true,
            });
        }
    };

    // Definir el arreglo de items de reporte con sus configuraciones
    const reportItems = [
        {
            action: ACTIONS['reports']['AbsenceInterruption'],
            label: langResources[
                'header.reports.marking.report.unattendance.interruptions.subitem'
            ],
            handler: () =>
                handleReportClick(
                    setIsOpenFilterUnattendanceInterruptionsReportModal
                ),
        },
        {
            action: ACTIONS['reports'][
                'WorkshiftAndInterruptionSchedulingAuditReport'
            ],
            label: 'Auditoría de Programación de Turnos e Interrupciones',
            handler: handlerSaveReportWorkshiftAndInterruptionSchedulingAuditReport,
        },
        {
            action: ACTIONS['reports']['ConsolidatedNumberPeoplePerWorkshift'],
            label: langResources[
                'header.reports.marking.report.consolidated.number.people.workshift'
            ],
            handler: handlerSaveReportConsolidatedNumberPeoplePerWorkshift,
        },
        {
            action: ACTIONS['reports']['LunchTimeControl'],
            label: langResources[
                'header.reports.marking.report.control.lunch.subitem'
            ],
            handler: () => handleReportClick(setIsOpenFilterLunchTimeControl),
        },
        {
            action: ACTIONS['reports']['DetailedProgrammingEventsReport'],
            label: 'Detallado de programación y eventos',
            handler: handlerSaveReportDetailedProgrammingEventsReport,
        },
        {
            action: ACTIONS['reports']['InterruptionDetail'],
            label: langResources[
                'header.reports.marking.report.interruptions.details.subitem'
            ],
            handler: () => handleReportClick(setIsOpenFilterInterruptionsRepotModal),
        },
        {
            action: ACTIONS['reports']['EventsReport'],
            label: langResources['header.reports.marking.report.events.subitem'],
            handler: handlerSaveReportAllEvents,
        },
        {
            action: ACTIONS['reports']['lateArrivalsEarlyDepartures'],
            label: langResources[
                'header.reports.marking.report.late.arrivals.earlydepartures.calc.subitem'
            ],
            handler: handlerSaveReportLateArrivalsEarlyDeparturesCalc,
        },
        {
            action: ACTIONS['reports']['lateArrivalsEarlyDeparturesGroup'],
            label: langResources[
                'header.reports.marking.report.late.arrivals.earlydepartures.subitem'
            ],
            handler: handlerSaveReportLateArrivalsEarlyDepartures,
        },
        {
            action: ACTIONS['reports']['DettlementHoursScheduling'],
            label: 'Horas Liquidadas por Programación',
            handler: handlerSaveReportDettlementScheduling,
        },
        {
            action: ACTIONS['reports']['AdditionalHoursInterruptions'],
            label: langResources[
                'header.reports.marking.report.additional.hours.interruptions'
            ],
            handler: handlerSaveReportAdditionalHoursInterruptions,
        },
        {
            action: ACTIONS['reports']['DailyAttendanceSheetReport'],
            label: langResources['header.reports.marking.report.template.subitem'],
            handler: () => handleReportClick(setIsOpenFilterDailyAssistance),
        },
        {
            action: ACTIONS['reports']['WorkShiftScheduling'],
            label: langResources[
                'header.reports.marking.report.workshift.scheduling.subitem'
            ],
            handler: () =>
                handleReportClick(setIsOpenFilterWorkingSchedulingsRepotModal),
        },
        {
            action: ACTIONS['reports']['PermanenceReport'],
            label: langResources['header.reports.marking.report.permanence.subitem'],
            handler: handlerSaveReportPermanence,
        },
        {
            action: ACTIONS['reports']['TimeWorked'],
            label: langResources[
                'header.reports.marking.report.working.time.subitem'
            ],
            handler: handlerSaveReportWorkTime,
        },
        {
            action: ACTIONS['reports']['TotalHoursWorked'],
            label: langResources['header.reports.marking.report.total.hours.worked'],
            handler: handlerSaveReportAverageHoursWork,
        },
        {
            action: ACTIONS['reports']['TimesInterruptions'],
            label: langResources[
                'header.reports.marking.report.interruptions.times.subitem'
            ],
            handler: handlerSaveReportInterruptionTimes,
        },
    ];

    return (
        <li className='nav-item px-4'>
            <a
                className='nav-link text-dark d-flex align-items-center justify-content-between'
                data-bs-toggle='collapse'
                href='#'
            >
                <span>{langResources['header.reports.marking.report.item']}</span>
                <i className='ri-arrow-drop-right-line fs-5'></i>
            </a>
            <div id='sidebarEcommerce' className='menu-dropdown collapse p-1'>
                <SimpleBar
                    style={{
                        direction: 'ltr',
                        width: '100%',
                        maxHeight: 'calc(100vh - 300px)',
                        height: 'calc(100vh - 300px)',
                    }}
                >
                    <ul className='nav nav-sm flex-column px-3'>
                        {reportItems.map(
                            (item, index) =>
                                getActionService(userInfo.actions, item.action) && (
                                    <ReportItem
                                        key={index}
                                        label={item.label}
                                        handler={item.handler}
                                    />
                                )
                        )}
                    </ul>
                </SimpleBar>
            </div>
        </li>
    );
};
